import React, { useState, useEffect, useCallback } from 'react';
import { Post } from '@contentflow/shared-types';
import { Link } from 'react-router-dom';
import './BlogHomepage.css';

interface BlogHomepageProps {
  apiBaseUrl?: string;
}

const BlogHomepage: React.FC<BlogHomepageProps> = ({ apiBaseUrl }) => {
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [settings, setSettings] = useState({ headScripts: '', footerScripts: '', blogHeaderBanner: '' });
  const [siteConfig, setSiteConfig] = useState({
    site_title: 'SolveSmartHome',
    site_tagline: 'Solutions from the smart home community.',
    site_description: 'We distill r/homeautomation and r/smarthome into actionable fixes. Find the right gear to solve lag, offline devices, and ecosystem lock-in.',
    site_categories: ['Smart Hubs', 'Zigbee', 'Matter', 'WiFi Devices', 'Voice Assistants', 'Lighting', 'Security', 'Local Control'],
    site_footer_text: 'Stay updated with the latest smart home fixes.'
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [postsPerPage] = useState(6);
  const [email, setEmail] = useState('');
  const [emailSubmitting, setEmailSubmitting] = useState(false);
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Use only configured categories (plus "All") for a clean, intentional filter experience
  const allCategories = ['All', ...Array.from(new Set(siteConfig.site_categories))];

  const fetchSettings = useCallback(async () => {
    try {
      console.log('Fetching settings from:', `${apiBaseUrl}/settings`);
      const response = await fetch(`${apiBaseUrl}/settings`);
      console.log('Settings response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (response.ok) {
        setSettings({
          headScripts: data.data.head_scripts || '',
          footerScripts: data.data.footer_scripts || '',
          blogHeaderBanner: data.data.blog_header_banner_base64 || ''
        });
      }
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  }, [apiBaseUrl]);

  const fetchSiteConfig = useCallback(async () => {
    try {
      console.log('Fetching config from:', `${apiBaseUrl}/config`);
      const response = await fetch(`${apiBaseUrl}/config`);
      console.log('Config response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (response.ok && data.success) {
        setSiteConfig(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch site config:', err);
    }
  }, [apiBaseUrl]);

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      console.log('Fetching posts from:', `${apiBaseUrl}/posts`);
      const response = await fetch(`${apiBaseUrl}/posts`);
      console.log('Response status:', response.status);
      console.log('Response headers:', response.headers);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (response.ok && data.success) {
        const postsData = Array.isArray(data.data) ? data.data : [];
        setPosts(postsData);
      } else {
        setError(data.message || 'Failed to fetch posts');
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl]);

  useEffect(() => {
    fetchPosts();
    fetchSettings();
    fetchSiteConfig();

    // Set page title and meta description for SEO
    document.title = `${siteConfig.site_title} - Smart Home Solutions`;
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', siteConfig.site_description);
    } else {
      const meta = document.createElement('meta');
      meta.name = 'description';
      meta.content = siteConfig.site_description;
      document.head.appendChild(meta);
    }

    // Add structured data for better SEO and AI understanding
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": `${siteConfig.site_title} Blog`,
      "description": siteConfig.site_description,
      "url": window.location.origin,
      "publisher": {
        "@type": "Organization",
        "name": siteConfig.site_title,
        "url": window.location.origin
      },
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": window.location.origin
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    return () => {
      // Cleanup
      const existingScript = document.querySelector('script[type="application/ld+json"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [fetchPosts, fetchSettings, fetchSiteConfig, siteConfig.site_description, siteConfig.site_title]);

  // Filter posts when category or posts change
  useEffect(() => {
    if (selectedCategory === 'All') {
      setFilteredPosts(posts);
    } else {
      setFilteredPosts(posts.filter(post =>
        post.tags && post.tags.split(',').some(tag => tag.trim() === selectedCategory)
      ));
    }
    setCurrentPage(1); // Reset to first page when filtering
  }, [selectedCategory, posts]);

  // Cleanup: Always remove any divs and identity.js from head tag (invalid HTML / not needed for Step 1)
  useEffect(() => {
    const cleanup = () => {
      // Remove divs from head (invalid HTML)
      const allHeadDivs = document.head.querySelectorAll('div');
      allHeadDivs.forEach(div => div.remove());

      // Remove identity.js scripts (not needed for Step 1 integration)
      const identityScripts = document.head.querySelectorAll('script[src*="identity.js"]');
      identityScripts.forEach(script => script.remove());
    };

    // Run immediately and set up interval to check periodically (every 500ms)
    cleanup();
    const interval = setInterval(cleanup, 500);

    return () => clearInterval(interval);
  }, []); // Run on mount and cleanup on unmount

  // Inject head scripts
  useEffect(() => {
    // Always clean up any divs in head (invalid HTML) - run this on every render
    const allHeadDivs = document.head.querySelectorAll('div');
    allHeadDivs.forEach(div => div.remove());

    if (settings.headScripts) {
      // Remove any existing scripts and containers first
      const existingScripts = document.querySelectorAll('#contentflow-head-scripts');
      existingScripts.forEach(script => script.remove());

      // Remove any div containers that might have been left in head (invalid HTML)
      const existingDivs = document.head.querySelectorAll('div#contentflow-head-scripts, div[id="contentflow-head-scripts"]');
      existingDivs.forEach(div => div.remove());

      // Remove any existing Ezoic scripts to prevent conflicts
      const existingEzoicScripts = document.querySelectorAll('script[src*="ezoic"], script[src*="gatekeeperconsent"], script[src*="ezojs"]');
      existingEzoicScripts.forEach(script => script.remove());

      // Find the insertion point: after title tag, before CSS/JS
      const head = document.head;
      const titleTag = head.querySelector('title');
      const insertBefore = head.querySelector('link[rel="stylesheet"]') ||
        head.querySelector('script[src*="main."]') ||
        head.querySelector('script[src*="static/"]') ||
        null;

      // Get insertion reference: after title, or after last meta/link if no title
      let insertAfter: Node | null = null;
      if (titleTag) {
        insertAfter = titleTag;
      } else {
        // Fallback: find last meta or link tag
        const allMetaLinks = Array.from(head.querySelectorAll('meta, link'));
        if (allMetaLinks.length > 0) {
          insertAfter = allMetaLinks[allMetaLinks.length - 1];
        }
      }

      // Parse and execute scripts
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = settings.headScripts;

      // Extract and categorize scripts in the exact order needed
      const allScripts = Array.from(tempDiv.querySelectorAll('script'));

      // Filter out identity.js (not needed for Step 1 integration)
      const filteredScripts = allScripts.filter(script =>
        !(script.src && script.src.includes('identity.js'))
      );

      // 1. Privacy scripts (gatekeeperconsent/privacy/__gpp)
      const privacyScripts = filteredScripts.filter(script =>
        (script.src && (script.src.includes('gatekeeperconsent') || script.src.includes('privacy'))) ||
        (script.textContent && (script.textContent.includes('__gpp') || script.textContent.includes('privacy')))
      );

      // 2. sa.min.js (Ezoic header script)
      const saMinScripts = filteredScripts.filter(script =>
        script.src && script.src.includes('sa.min.js')
      );

      // 3. ezstandalone initialization scripts (inline initialization)
      const ezstandaloneScripts = filteredScripts.filter(script =>
        script.textContent && script.textContent.includes('ezstandalone') &&
        !privacyScripts.includes(script) && !saMinScripts.includes(script)
      );

      // 4. Other scripts (excluding identity.js which was already filtered)
      const otherScripts = filteredScripts.filter(script =>
        !privacyScripts.includes(script) &&
        !saMinScripts.includes(script) &&
        !ezstandaloneScripts.includes(script)
      );

      // Insert scripts in the exact order: privacy -> sa.min.js -> ezstandalone -> others
      const orderedScripts = [...privacyScripts, ...saMinScripts, ...ezstandaloneScripts, ...otherScripts];

      orderedScripts.forEach(script => {
        const newScript = document.createElement('script');

        if (script.src) {
          newScript.src = script.src;
          // Preserve async/defer attributes
          if (script.async) newScript.async = true;
          if (script.defer) newScript.defer = true;
          // sa.min.js should have async attribute
          if (script.src.includes('sa.min.js') && !script.async) {
            newScript.async = true;
          }
          // Add data-cfasync="false" for all external scripts
          newScript.setAttribute('data-cfasync', 'false');
        } else {
          newScript.textContent = script.textContent || '';
          // Add data-cfasync="false" for privacy and ezstandalone inline scripts
          if (script.textContent && (script.textContent.includes('__gpp') ||
            script.textContent.includes('privacy') ||
            script.textContent.includes('ezstandalone'))) {
            newScript.setAttribute('data-cfasync', 'false');
          }
        }

        // Insert at the correct position
        if (insertBefore) {
          head.insertBefore(newScript, insertBefore);
        } else if (insertAfter) {
          // Insert after the reference node
          if (insertAfter.nextSibling) {
            head.insertBefore(newScript, insertAfter.nextSibling);
          } else {
            head.appendChild(newScript);
          }
        } else {
          head.appendChild(newScript);
        }
      });

      // Note: Non-script content (like HTML comments, text nodes, etc.) is ignored
      // Only script elements are inserted into the head tag

      // After Ezoic scripts are injected, ensure app's main JS loads AFTER Ezoic scripts
      // Order should be: Ezoic scripts -> CSS -> App's main JS
      const allHeadScripts = Array.from(head.querySelectorAll('script'));
      const appMainScripts = allHeadScripts.filter(script =>
        script.src && (script.src.includes('/static/js/main.') || script.src.includes('/main.') || script.src.match(/\/main\.[a-f0-9]+\.js/))
      );

      // Find the last Ezoic script (to insert app JS after it)
      const allEzoicScripts = allHeadScripts.filter(script =>
        script.src && (script.src.includes('ezoic') || script.src.includes('gatekeeperconsent') || script.src.includes('ezojs')) ||
        script.textContent && (script.textContent.includes('ezstandalone') || script.textContent.includes('__gpp'))
      );

      if (allEzoicScripts.length > 0 && appMainScripts.length > 0) {
        const lastEzoicScript = allEzoicScripts[allEzoicScripts.length - 1];
        const cssLink = head.querySelector('link[rel="stylesheet"]');

        // Move app's main JS to after Ezoic scripts (and after CSS if CSS exists and is after Ezoic)
        appMainScripts.forEach(script => {
          const scriptIndex = Array.from(head.childNodes).indexOf(script);
          const lastEzoicIndex = Array.from(head.childNodes).indexOf(lastEzoicScript);

          // If script is before Ezoic scripts, move it after
          if (scriptIndex < lastEzoicIndex) {
            // Determine insertion point: after CSS if CSS exists and is after Ezoic, otherwise after Ezoic
            let insertAfter: Node = lastEzoicScript;
            if (cssLink) {
              const cssIndex = Array.from(head.childNodes).indexOf(cssLink);
              if (cssIndex > lastEzoicIndex) {
                // CSS is after Ezoic, so insert JS after CSS
                insertAfter = cssLink;
              }
            }

            if (insertAfter.nextSibling) {
              head.insertBefore(script, insertAfter.nextSibling);
            } else {
              head.appendChild(script);
            }
          }
        });
      }

      // Also remove any identity.js scripts that might have been added
      const identityScripts = head.querySelectorAll('script[src*="identity.js"]');
      identityScripts.forEach(script => script.remove());
    }
  }, [settings.headScripts]);

  // Inject footer scripts
  useEffect(() => {
    if (settings.footerScripts) {
      // Remove any existing scripts first
      const existingScripts = document.querySelectorAll('#contentflow-footer-scripts');
      existingScripts.forEach(script => script.remove());

      // Create a container div
      const container = document.createElement('div');
      container.id = 'contentflow-footer-scripts';
      container.style.display = 'none'; // Hide the container

      // Parse and execute scripts
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = settings.footerScripts;

      // Extract and execute each script
      const scripts = tempDiv.querySelectorAll('script');
      scripts.forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
          newScript.async = script.async;
          newScript.defer = script.defer;
        } else {
          newScript.textContent = script.textContent;
        }
        document.body.appendChild(newScript);
      });

      // Add any non-script content to the container
      const nonScripts = Array.from(tempDiv.childNodes).filter(node =>
        node.nodeType !== Node.ELEMENT_NODE || (node as Element).tagName !== 'SCRIPT'
      );
      nonScripts.forEach(node => container.appendChild(node.cloneNode(true)));

      document.body.appendChild(container);
    }
  }, [settings.footerScripts]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatReadingTime = (content: string) => {
    const wordsPerMinute = 200;
    const wordCount = content.replace(/<[^>]*>/g, '').split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readingTime} min`;
  };

  // Pagination logic
  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const startIndex = (currentPage - 1) * postsPerPage;
  const endIndex = startIndex + postsPerPage;
  const currentPosts = filteredPosts.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setEmailError('Please enter your email address.');
      return;
    }
    setEmailError('');
    setEmailSubmitting(true);
    try {
      const response = await fetch(`${apiBaseUrl}/email-subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: trimmedEmail,
          source: 'homepage',
          timestamp: new Date().toISOString()
        })
      });
      if (response.ok) {
        setEmailSubmitted(true);
        setEmail('');
      } else {
        setEmailError('Something went wrong. Please try again.');
      }
    } catch (err) {
      setEmailError('Something went wrong. Please try again.');
    } finally {
      setEmailSubmitting(false);
    }
  };

  return (
    <div className="blog-homepage">
      {/* Skip to main content for accessibility */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {settings.blogHeaderBanner && settings.blogHeaderBanner.trim() !== '' ? (
        <header className="blog-header-banner" role="banner">
          <div className="banner-image-container">
            <img
              src={settings.blogHeaderBanner}
              alt={`${siteConfig.site_title} Header Banner`}
              className="banner-image"
              loading="eager"
              onError={(e) => {
                // Fallback if image fails to load
                console.error('Banner image failed to load');
                e.currentTarget.style.display = 'none';
              }}
            />
            <div className="banner-tagline-overlay">
              <div className="banner-content">
                <h1 className="blog-title">
                  <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                    {siteConfig.site_title}
                  </Link>
                </h1>
                <p className="tagline">{siteConfig.site_tagline}</p>
              </div>
              <nav className="blog-nav" role="navigation" aria-label="Main navigation">
                <Link to="/cms" className="nav-link" aria-label="Content Management System">
                  Login
                </Link>
              </nav>
            </div>
          </div>
        </header>
      ) : (
        <header className="blog-header" role="banner">
          <div className="header-content">
            <div className="logo-section">
              <h1 className="blog-title">
                <Link to="/" aria-label={`${siteConfig.site_title} Blog - Home`}>
                  {siteConfig.site_title}
                </Link>
              </h1>
              <p className="tagline">{siteConfig.site_tagline}</p>
            </div>
            <nav className="blog-nav" role="navigation" aria-label="Main navigation">
              <Link to="/cms" className="nav-link" aria-label="Content Management System">
                Login
              </Link>
            </nav>
          </div>
        </header>
      )}

      <main id="main-content" className="blog-main" role="main">
        {/* Category Filter */}
        <div className="category-filter">
          <div className="filter-content">
            {allCategories.map((category) => (
              <button
                key={category}
                className={`filter-button ${selectedCategory === category ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category)}
                aria-label={`Filter by ${category}`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Email Capture */}
        <section className="email-capture" aria-labelledby="email-capture-heading">
          <div className="email-capture-inner">
            <h2 id="email-capture-heading" className="email-capture-title">
              Get the best smart home fixes in your inbox
            </h2>
            <p className="email-capture-text">
              Weekly home automation solutions. No spam, just the fixes that work.
            </p>
            {emailSubmitted ? (
              <div className="email-capture-success" role="status">
                Thanks! Check your inbox for the first edition.
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="email-capture-form" noValidate>
                <div className="email-capture-field">
                  <label htmlFor="email-signup" className="sr-only">
                    Email address
                  </label>
                  <input
                    id="email-signup"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email"
                    className="email-capture-input"
                    disabled={emailSubmitting}
                    autoComplete="email"
                    aria-describedby={emailError ? 'email-error' : undefined}
                    aria-invalid={!!emailError}
                  />
                  <button
                    type="submit"
                    className="email-capture-button"
                    disabled={emailSubmitting}
                    aria-label="Subscribe"
                  >
                    {emailSubmitting ? 'Subscribing...' : 'Subscribe'}
                  </button>
                </div>
                {emailError && (
                  <p id="email-error" className="email-capture-error" role="alert">
                    {emailError}
                  </p>
                )}
              </form>
            )}
          </div>
        </section>

        {/* Posts Grid */}
        <div className="posts-container">
          {loading && (
            <div className="loading" role="status" aria-live="polite">
              <div className="spinner" aria-hidden="true"></div>
              <p>Loading...</p>
            </div>
          )}

          {error && (
            <div className="error-message" role="alert">
              <div className="error-icon" aria-hidden="true">⚠️</div>
              <p>{error}</p>
              <button onClick={fetchPosts} className="retry-button">
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && (
            <>
              {currentPosts.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-icon" aria-hidden="true">📚</div>
                  <h3>No articles found</h3>
                  <p>Try selecting a different category or check back later for new content.</p>
                </div>
              ) : (
                <div className="posts-grid" role="list">
                  {currentPosts.map((post, index) => (
                    <article
                      key={post.id}
                      className="post-card"
                      role="listitem"
                      itemScope
                      itemType="https://schema.org/BlogPosting"
                    >
                      <Link
                        to={`/post/${post.slug}`}
                        className="post-content"
                        itemProp="url"
                        aria-label={`Read article: ${post.title}`}
                      >
                        <div className="post-meta-top">
                          <div className="post-category">
                            {post.tags ? post.tags.split(',')[0].trim() : 'General'}
                          </div>
                          <div className="post-date">
                            {formatDate(post.published_at || post.created_at)}
                          </div>
                        </div>

                        {(post.featured_image_url || post.featured_image_base64) && (
                          <div className="post-image">
                            <img
                              src={post.featured_image_base64 || post.featured_image_url}
                              alt={post.title}
                              className="post-thumbnail"
                              itemProp="image"
                            />
                          </div>
                        )}

                        <h2 className="post-title" itemProp="headline">
                          {post.title}
                        </h2>

                        <div className="post-meta-bottom">
                          <div className="post-stats">
                            <span className="reading-time">
                              {formatReadingTime(post.body)}
                            </span>
                            <span className="post-views">
                              {post.views} views
                            </span>
                          </div>
                        </div>
                      </Link>
                    </article>
                  ))}
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>

                  <div className="pagination-numbers">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <button
                        key={page}
                        className={`pagination-number ${currentPage === page ? 'active' : ''}`}
                        onClick={() => handlePageChange(page)}
                        aria-label={`Go to page ${page}`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="pagination-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <footer className="blog-footer" role="contentinfo">
        <div className="footer-content">
          <div className="footer-section">
            <h3 className="footer-title">Categories</h3>
            <nav className="footer-nav" aria-label="Category navigation">
              {allCategories.slice(1).map((category) => (
                <button
                  key={category}
                  className="footer-link"
                  onClick={() => setSelectedCategory(category)}
                >
                  {category}
                </button>
              ))}
            </nav>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Pages</h3>
            <nav className="footer-nav" aria-label="Page navigation">
              <Link to="/" className="footer-link">Home</Link>
              <Link to="/about" className="footer-link">About</Link>
              <Link to="/privacy" className="footer-link">Privacy</Link>
              <Link to="/cms" className="footer-link">Login</Link>
              <a href="#main-content" className="footer-link">Back to Top</a>
            </nav>
          </div>

          <div className="footer-section">
            <h3 className="footer-title">Connect</h3>
            <p className="footer-text">
              {siteConfig.site_footer_text}
            </p>
          </div>
        </div>

        <div className="footer-bottom">
          <p>&copy; 2024 {siteConfig.site_title} Blog. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default BlogHomepage;
